import { fetchEventSource } from '@microsoft/fetch-event-source';

import { useAuth } from '@/composables/Auth.ts';
import { getAccessToken } from '@/composables/SessionStorage.ts';

interface EventStreamOptions {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  onMessage?: (data: unknown) => void;
  onError?: (error: Error) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onActivity?: () => void;
  maxRetries?: number;
  signal?: AbortSignal;
}

const fetchReportingActivity =
  (onActivity: () => void): typeof fetch =>
  async (input, init) => {
    const response = await fetch(input, init);
    if (!response.body) return response;
    const body = response.body.pipeThrough(
      new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
          onActivity();
          controller.enqueue(chunk);
        },
      }),
    );
    return new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  };

export const createEventStream = (options: EventStreamOptions) => {
  const { refreshToken } = useAuth();
  const signal = options.signal || new AbortController().signal;
  let failureCount = 0;

  const token = getAccessToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  return fetchEventSource(options.url, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal,
    // Prevent reconnections for POST requests
    openWhenHidden: true,
    // Keep-alive comments never reach onmessage; counting received bytes lets callers see them.
    fetch: options.onActivity ? fetchReportingActivity(options.onActivity) : undefined,
    onmessage(ev) {
      try {
        if (!ev.data) return;
        const data = JSON.parse(ev.data);
        if (options.onMessage) {
          options.onMessage(data);
        }
        failureCount = 0;
      } catch (error) {
        console.error('Failed to parse SSE event:', error, ev.data);
      }
    },
    onerror(error) {
      failureCount += 1;

      if (options.onError) {
        options.onError(error);
      }

      if (failureCount <= (options.maxRetries || 10)) {
        refreshToken().catch(() => {
          throw new Error('Failed to refresh token');
        });
      }

      throw error;
    },
    async onopen(response) {
      // onopen fires for any response, 4xx/5xx included; a 502 would look like a stream that closes at once and loops reconnects.
      if (!response.ok) {
        throw new Error(`Stream returned ${response.status}`);
      }
      if (options.onOpen) {
        options.onOpen();
      }
    },
    onclose() {
      if (options.onClose) {
        options.onClose();
      }
    },
  });
};
